import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, clearToken } from "../lib/api";
import { DomainProvider, useDomains } from "../lib/domain-context";
import { LayoutDashboard, Users, List, FileText, Send, Settings as SettingsIcon, UserPlus, LogOut, Mail, BarChart3, ShieldOff, Menu, X, Globe } from "lucide-react";

interface Me { user: { id: string; email: string; name: string; role: string; senderLocalPart: string } }

export default function PortalShell() {
  return (
    <DomainProvider>
      <PortalShellInner />
    </DomainProvider>
  );
}

function PortalShellInner() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/api/auth/me") });

  // Auto-close the drawer when the route changes — without this the
  // drawer stays open on mobile after tapping a nav item.
  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  const isAdmin = data?.user.role === "super_admin" || data?.user.role === "admin";

  function logout() {
    clearToken();
    // Drop ALL cached queries — otherwise the next user (including a
    // re-login in the same tab as a different account) sees stale data
    // from the previous session, including a stale "me" that briefly
    // shows the old role and admin sidebar items until refetch.
    qc.clear();
    nav("/login", { replace: true });
  }

  const items: Array<{ to: string; label: string; icon: React.ComponentType<{ size?: number }> }> = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/contacts", label: "Contacts", icon: Users },
    { to: "/lists", label: "Lists", icon: List },
    { to: "/templates", label: "Templates", icon: FileText },
    { to: "/campaigns", label: "Campaigns", icon: Send },
    { to: "/reports", label: "Reports", icon: BarChart3 },
  ];
  const adminItems = [
    { to: "/users", label: "Users", icon: UserPlus },
    { to: "/domains", label: "Domains", icon: Globe },
    { to: "/suppression", label: "Suppression", icon: ShieldOff },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile backdrop — visible only when drawer is open on small screens */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-white border-r border-gray-200 flex flex-col
                         transform transition-transform lg:translate-x-0
                         ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <NavLink to="/profile" className="px-5 py-4 border-b border-gray-200 hover:bg-gray-50 block">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Mail size={16} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">WenMail Notify</div>
              <div className="text-xs text-gray-500 truncate">{data?.user.email ?? "…"}</div>
            </div>
          </div>
        </NavLink>
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-600 hover:bg-gray-100"
                }`}>
              <it.icon size={16} /><span>{it.label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="pt-4 pb-1 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Admin</div>
              {adminItems.map((it) => (
                <NavLink key={it.to} to={it.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-600 hover:bg-gray-100"
                    }`}>
                  <it.icon size={16} /><span>{it.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <button onClick={logout}
          className="m-2 flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
          <LogOut size={16} /> Sign out
        </button>
      </aside>
      <main className="flex-1 overflow-y-auto min-w-0">
        <TopBar onOpenSidebar={() => setSidebarOpen(true)} />
        <Outlet />
      </main>
    </div>
  );
}

interface Quota {
  workspaceToday: number; workspaceDailyCap: number; workspaceRemainingToday: number;
  userToday: number; activeCampaigns: number;
  brevo: { ok: true; planType: string | null; remainingCredits: number | null; companyName: string | null }
        | { ok: false; reason: string };
  resetsAt: string;
}

function DomainSwitcher() {
  const { domains, activeId, setActiveId, loading } = useDomains();
  if (loading) return null;
  if (domains.length === 0) {
    return <span className="text-amber-600 text-xs px-2 py-1 bg-amber-50 rounded">No sender domain — ask an admin</span>;
  }
  if (domains.length === 1) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded">
        <Globe size={11} className="text-gray-400" /> {domains[0].senderDomain}
      </span>
    );
  }
  return (
    <select value={activeId ?? ""} onChange={(e) => setActiveId(e.target.value)}
      className="border border-gray-300 rounded-md text-xs px-2 py-1 bg-white focus:outline-none focus:border-indigo-500">
      {domains.map((d) => (
        <option key={d.notifyDomainId} value={d.notifyDomainId}>{d.senderDomain}</option>
      ))}
    </select>
  );
}

function TopBar({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const { activeId } = useDomains();
  const { data } = useQuery({
    queryKey: ["quota", activeId],
    queryFn: () => api<Quota>(`/api/quota${activeId ? `?domain=${activeId}` : ""}`),
    refetchInterval: 30_000,
  });

  if (!data) {
    return (
      <div className="h-12 border-b border-gray-200 bg-white flex items-center px-3 lg:px-6 gap-3">
        <button onClick={onOpenSidebar} className="lg:hidden p-2 -ml-2 text-gray-600">
          <Menu size={18} />
        </button>
        <div className="flex-1" />
        <DomainSwitcher />
      </div>
    );
  }

  const pct = data.workspaceDailyCap > 0 ? (data.workspaceToday / data.workspaceDailyCap) * 100 : 0;
  const tone = pct >= 95 ? "bg-red-100 text-red-700 border-red-200"
             : pct >= 75 ? "bg-amber-100 text-amber-700 border-amber-200"
             : "bg-gray-100 text-gray-700 border-gray-200";

  const ms = new Date(data.resetsAt).getTime() - Date.now();
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  const resetIn = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;

  const creditLine = data.brevo.ok && data.brevo.remainingCredits !== null
    ? `${data.brevo.remainingCredits} credit${data.brevo.remainingCredits === 1 ? "" : "s"} left this period`
    : null;

  return (
    <div className="border-b border-gray-200 bg-white px-3 lg:px-6 py-2 flex items-center gap-3 text-xs">
      {/* Hamburger on mobile, hidden on lg+ where the sidebar is permanent */}
      <button onClick={onOpenSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 shrink-0">
        <Menu size={18} />
      </button>

      {/* Spacer pushes everything to the right on desktop, but on mobile
          the row may wrap so just keep small gap. */}
      <div className="flex-1" />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <DomainSwitcher />
        <div className="text-gray-500 hidden sm:block">You today: <span className="font-medium text-gray-900">{data.userToday}</span></div>
        <div className={`${tone} border rounded-full px-3 py-1 flex items-center gap-1.5 whitespace-nowrap`}>
          <span className="hidden sm:inline">Workspace:</span>
          <span className="font-semibold">{data.workspaceToday}/{data.workspaceDailyCap}</span>
          <span className="text-gray-500 hidden md:inline">· resets in {resetIn}</span>
        </div>
        {creditLine && <div className="text-gray-500 hidden md:block">{creditLine}</div>}
      </div>
    </div>
  );
}
